import { useCallback } from 'react'
import { ArrowLeftRight, Calculator, ClipboardCopy, Coins, Download, History, ShoppingBasket, Sparkles } from 'lucide-react'
import { usePermissions, useToast } from '../admin/primitives.js'
import { telechargerFichier } from '../admin/telechargement.js'
import { copierTexte, entreesLien, useMenuContextuel, type ActionMenu, type GroupeMenu } from '../shell/menu-contextuel.js'
import { useGroupeFavori } from '../shell/menu-favori.js'
import { urlCloture } from '../finance/nights-common.js'
import type { ReponseCaisse } from './caisse-nuit-model.js'
import { allerALaPrecedente } from './ComparerPrecedente.js'
import { droitsMenuSoiree, planNuit, planSoiree, titreNuit, type NuitMenu, type SoireeMenu } from './menu-soiree-modele.js'

export { nuitDeLigne, nuitDuChiffreAffaires, type NuitMenu, type SoireeMenu } from './menu-soiree-modele.js'

/**
 * MENU CLIC DROIT DES SOIRÉES ET DES NUITS DE COMPTABILITÉ (lot 2, 24/10/2026).
 *
 * Navigation, copie et les gestes de lecture que les fiches portent déjà
 * (voir `menu-soiree-modele.ts` pour les adresses et les droits) :
 *
 *   · une soirée : Ouvrir, l'onglet, « Comparer à une autre soirée… »,
 *     « Comparer à la précédente » (la précédente lue au clic, par la même
 *     lecture que le bouton de la fiche : `allerALaPrecedente`), la nuit en
 *     comptabilité quand l'écran a lu la caisse, « Exporter la soirée en
 *     CSV », l'ajout aux favoris, copier le nom et le lien ;
 *   · une nuit : Ouvrir, l'onglet, Voir ou Reprendre la clôture, ses soirées,
 *     les ventes de cette nuit, « Exporter la nuit en CSV », l'ajout aux
 *     favoris, copier la date et le lien.
 *
 * Les exports passent par `telechargerFichier`, la fonction du bouton
 * « Exporter CSV » des fiches : le menu ne lit jamais rien lui-même, et le
 * serveur fabrique le fichier sous ses propres droits. Le seul geste
 * d'écriture est l'épingle des favoris (`useGroupeFavori`, celle de l'étoile).
 *
 * Aucune autre écriture : commencer la clôture, valider la nuit ou rattacher
 * une soirée restent sur leurs pages, avec leurs confirmations. Le serveur
 * reste l'arbitre ; une entrée affichée ne vaut jamais autorisation.
 *
 * Jamais sur une soirée Billetterie Live Club (/soirees/billetterie/:id) :
 * ce n'est pas une soirée du domaine canonique, la comparaison y répondrait 404.
 */

/**
 *     const menuSoiree = useMenuSoiree()
 *     <Link href={`/soirees/${s.id}`} {...menuSoiree.soiree(s, caisse)}>
 *     <Link href={`/comptabilite/nuits/${n.business_date}`} {...menuSoiree.nuit(nuitDeLigne(n))}>
 *
 * `caisse` : la caisse de la nuit que la page a déjà lue (`useCaisseNuit`),
 * sans quoi « Ouvrir la nuit en comptabilité » n'est pas proposée.
 */
export function useMenuSoiree() {
  const menu = useMenuContextuel()
  const toast = useToast()
  const { permissions } = usePermissions()
  const favori = useGroupeFavori()

  const actionsSoiree = useCallback((s: SoireeMenu, caisse?: ReponseCaisse | null): GroupeMenu[] => {
    const p = planSoiree(s, droitsMenuSoiree(permissions), caisse)
    const lien = entreesLien(p.fiche, toast, { ouvrir: 'Ouvrir la soirée' })
    const aller: ActionMenu[] = p.ouvrir ? lien.filter((a) => a.id !== 'c-lien') : []
    if (p.comparer) aller.push({ id: 'comparer', libelle: 'Comparer à une autre soirée…', icone: <ArrowLeftRight size={15} />, href: p.comparer })
    if (p.precedente) aller.push({ id: 'comparer-precedente', libelle: 'Comparer à la précédente', icone: <History size={15} />, agir: () => allerALaPrecedente(s.id, toast) })
    if (p.nuit) aller.push({ id: 'nuit', libelle: 'Ouvrir la nuit en comptabilité', icone: <Coins size={15} />, href: p.nuit })
    const exporter = p.exporter
    return [
      { actions: aller },
      { actions: exporter ? [{ id: 'export-csv', libelle: 'Exporter la soirée en CSV', icone: <Download size={15} />, agir: () => { void telechargerFichier(exporter, 'soiree.csv', toast) } }] : [] },
      ...(p.ouvrir ? [favori(p.fiche, s.name)] : []),
      { titre: 'Copier', actions: [
        { id: 'c-nom', libelle: 'Copier le nom', icone: <ClipboardCopy size={15} />, agir: () => copierTexte(s.name, 'nom', toast) },
        ...lien.filter((a) => a.id === 'c-lien'),
      ] },
    ]
  }, [permissions, toast, favori])

  const actionsNuit = useCallback((n: NuitMenu): GroupeMenu[] => {
    const p = planNuit(n, droitsMenuSoiree(permissions), urlCloture)
    const lien = entreesLien(p.fiche, toast, { ouvrir: 'Ouvrir la nuit' })
    const aller: ActionMenu[] = p.ouvrir ? lien.filter((a) => a.id !== 'c-lien') : []
    if (p.cloture) aller.push({ id: 'cloture', libelle: p.cloture.libelle, icone: <Calculator size={15} />, href: p.cloture.href })
    const autour: ActionMenu[] = []
    // Une soirée : l'entrée directe ; plusieurs : le sous-menu, comme « Changer de salle ».
    if (p.soiree) autour.push({ ...p.soiree, icone: <Sparkles size={15} /> })
    if (p.ventes) autour.push({ id: 'ventes', libelle: 'Ventes de cette nuit', icone: <ShoppingBasket size={15} />, href: p.ventes })
    const exporter = p.exporter
    if (exporter) autour.push({ id: 'export-csv', libelle: 'Exporter la nuit en CSV', icone: <Download size={15} />, agir: () => { void telechargerFichier(exporter, 'comptabilite-nuit.csv', toast) } })
    return [
      { actions: aller },
      { actions: autour },
      ...(p.ouvrir ? [favori(p.fiche, p.titre)] : []),
      { titre: 'Copier', actions: [
        { id: 'c-date', libelle: 'Copier la date', icone: <ClipboardCopy size={15} />, agir: () => copierTexte(p.date, 'date', toast) },
        ...lien.filter((a) => a.id === 'c-lien'),
      ] },
    ]
  }, [permissions, toast, favori])

  return {
    soiree: (s: SoireeMenu, caisse?: ReponseCaisse | null) => menu(() => actionsSoiree(s, caisse), s.name),
    nuit: (n: NuitMenu) => menu(() => actionsNuit(n), titreNuit(n.date)),
  }
}
